import * as Sentry from '@sentry/react';
import { ZoomMtg } from '@zoom/meetingsdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useMembersContext } from '@/contexts/membersContext';
import useMixpanel from '@/hooks/useTracking/useMixpanel';
import useTranslate from '@/hooks/useTranslate';
import Spinner from '@/ui/members/components/Spinner';

import { useAppointment } from '../../hooks/useAppointments';
import { useJoinVideoAppointment } from '../../hooks/useJoinVideoAppointment';
import { useLeaveVideoAppointment } from '../../hooks/useLeaveVideoAppointment';
import { useZoomToken } from '../../hooks/useZoomToken';
import { ZoomRole } from '../../types';
import { parseZoomURL } from '../../utils/parseZoomURL';
import './styles.css';

const ZoomMeeting = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { memberData, getMemberPublicName, addMessage } = useMembersContext();
  const { t } = useTranslate('pages.members.pages.ZoomMeeting');
  const { track } = useMixpanel();
  const navigate = useNavigate();

  const hasJoined = useRef(false);

  const [joinAt, setJoinAt] = useState<string>('');

  // Track muted and video off states and timers
  const isMutedRef = useRef(false);
  const isVideoOffRef = useRef(false);
  const mutedStartTimeRef = useRef<number | null>(null);
  const videoOffStartTimeRef = useRef<number | null>(null);
  const totalMutedMinutesRef = useRef(0);
  const totalVideoOffMinutesRef = useRef(0);

  const { data: appointmentData, isPending: isLoadingEvent } = useAppointment(appointmentId!);
  const { mutate: joinVideoAppointment } = useJoinVideoAppointment();
  const { mutate: leaveVideoAppointment } = useLeaveVideoAppointment();

  const meetingInfo = appointmentData?.appointment.zoom_join_url
    ? parseZoomURL(appointmentData.appointment.zoom_join_url)
    : null;

  const {
    data: tokenData,
    isLoading: isLoadingToken,
    refetch: refetchToken
  } = useZoomToken(meetingInfo?.meetingNumber, ZoomRole.ATTENDEE);

  // Preload Zoom SDK resources when component mounts
  useEffect(() => {
    try {
      ZoomMtg.preLoadWasm();
      ZoomMtg.prepareWebSDK();
    } catch (error) {
      Sentry.captureMessage('Error preloading Zoom SDK', { level: 'error' });
      console.error('Error preloading Zoom SDK:', error);
    }
  }, []);

  const appointmentDataRef = useRef(appointmentData);
  const memberDataRef = useRef(memberData);
  const joinAtRef = useRef(joinAt);

  useEffect(() => {
    appointmentDataRef.current = appointmentData;
  }, [appointmentData]);

  useEffect(() => {
    memberDataRef.current = memberData;
  }, [memberData]);

  useEffect(() => {
    joinAtRef.current = joinAt;
  }, [joinAt]);

  const handleAudioVideoStateChange = useCallback((muted: boolean, videoOff: boolean) => {
    const now = Date.now();

    // Handle muted state change
    if (muted && !isMutedRef.current) {
      mutedStartTimeRef.current = now;
      isMutedRef.current = true;
    } else if (!muted && isMutedRef.current) {
      if (mutedStartTimeRef.current !== null) {
        const mutedDurationMs = now - mutedStartTimeRef.current;
        const mutedDurationMinutes = mutedDurationMs / 1000 / 60;
        totalMutedMinutesRef.current += mutedDurationMinutes;
      }
      mutedStartTimeRef.current = null;
      isMutedRef.current = false;
    }

    // Handle video state change
    if (videoOff && !isVideoOffRef.current) {
      videoOffStartTimeRef.current = now;
      isVideoOffRef.current = true;
    } else if (!videoOff && isVideoOffRef.current) {
      if (videoOffStartTimeRef.current !== null) {
        const videoOffDurationMs = now - videoOffStartTimeRef.current;
        const videoOffDurationMinutes = videoOffDurationMs / 1000 / 60;
        totalVideoOffMinutesRef.current += videoOffDurationMinutes;
      }
      videoOffStartTimeRef.current = null;
      isVideoOffRef.current = false;
    }
  }, []);

  const handleUserJoin = useCallback(
    (joinType: 'meeting' | 'waiting_room') => {
      if (!appointmentDataRef.current?.appointment.id || !memberDataRef.current.person.id) {
        return;
      }

      const joinAtTime = new Date().toISOString();
      joinVideoAppointment({
        person_id: memberDataRef.current.person.id,
        appointment_id: appointmentDataRef.current.appointment.id,
        join_at: joinAtTime,
        leave_at: '',
        recorded_duration_in_minutes: 0,
        client: 'web',
        join_type: joinType
      });

      setJoinAt(joinAtTime);
      track('Joined ' + (joinType === 'meeting' ? 'meeting' : 'waiting room'), {
        appointmentId: appointmentDataRef.current?.appointment.id || '',
        feature: 'web-appointments'
      });
    },
    [joinVideoAppointment, track]
  );

  const handleUserLeave = useCallback(() => {
    if (
      !appointmentDataRef.current?.appointment.id ||
      !memberDataRef.current.person.id ||
      !joinAtRef.current
    ) {
      return;
    }

    // Stop any active timers and add them to totals
    const now = Date.now();
    if (isMutedRef.current && mutedStartTimeRef.current !== null) {
      const mutedDurationMs = now - mutedStartTimeRef.current;
      const mutedDurationMinutes = mutedDurationMs / 1000 / 60;
      totalMutedMinutesRef.current += mutedDurationMinutes;
    }
    if (isVideoOffRef.current && videoOffStartTimeRef.current !== null) {
      const videoOffDurationMs = now - videoOffStartTimeRef.current;
      const videoOffDurationMinutes = videoOffDurationMs / 1000 / 60;
      totalVideoOffMinutesRef.current += videoOffDurationMinutes;
    }

    // Call the leave mutation synchronously to ensure it completes
    // even though Zoom will redirect after this
    leaveVideoAppointment({
      person_id: memberDataRef.current.person.id,
      appointment_id: appointmentDataRef.current.appointment.id,
      join_at: joinAtRef.current,
      leave_at: new Date().toISOString(),
      recorded_duration_in_minutes: 0,
      client: 'web'
    });

    track('User left meeting', {
      appointmentId: appointmentDataRef.current.appointment.id,
      totalMutedMinutes: Math.round(totalMutedMinutesRef.current * 100) / 100,
      totalVideoOffMinutes: Math.round(totalVideoOffMinutesRef.current * 100) / 100,
      feature: 'web-appointments'
    });
  }, [leaveVideoAppointment, track]);

  useEffect(() => {
    const setUpEvents = () => {
      ZoomMtg.inMeetingServiceListener(
        'onUserIsInWaitingRoom',
        (data: { self: boolean; isInWaitingRoom: boolean }) => {
          console.log('onUserIsInWaitingRoom', data);
          if (data.self && data.isInWaitingRoom) {
            handleUserJoin('waiting_room');
          }
        }
      );

      ZoomMtg.inMeetingServiceListener(
        'onUserUpdate',
        (data: { muted: boolean; video: boolean }) => {
          handleAudioVideoStateChange(data.muted, !data.video);
        }
      );

      ZoomMtg.inMeetingServiceListener('onUserJoin', (data: { self: boolean; isHold: boolean }) => {
        if (data.self && !data.isHold) {
          handleUserJoin('meeting');
        }
      });

      ZoomMtg.inMeetingServiceListener('onUserLeave', () => {
        handleUserLeave();
      });

      ZoomMtg.inMeetingServiceListener('onJoinSpeed', (data: { level: number }) => {
        // userInWaitingForHost: 4
        if (data.level === 4) {
          handleUserJoin('waiting_room');
        }
      });
    };

    const tearDownEvents = () => {
      ZoomMtg.inMeetingServiceListener('onUserIsInWaitingRoom', () => {});
      ZoomMtg.inMeetingServiceListener('onUserUpdate', () => {});
      ZoomMtg.inMeetingServiceListener('onUserJoin', () => {});
      ZoomMtg.inMeetingServiceListener('onUserLeave', () => {});
      ZoomMtg.inMeetingServiceListener('onJoinSpeed', () => {});
    };

    setUpEvents();

    return () => {
      tearDownEvents();
    };
  }, [handleAudioVideoStateChange, handleUserJoin, handleUserLeave]);

  useEffect(() => {
    if (meetingInfo?.meetingNumber) {
      refetchToken();
    }
  }, [meetingInfo?.meetingNumber, refetchToken]);

  const getZoomToken = useCallback(() => {
    if (!tokenData) return null;
    if (Array.isArray(tokenData)) {
      return tokenData[0]?.data?.zoomSdkJwt || tokenData[0]?.zoomSdkJwt;
    }
    return tokenData.zoomSdkJwt;
  }, [tokenData]);

  const startMeeting = useCallback(
    (signature: string) => {
      const zmmtgRoot = document.getElementById('zmmtg-root');
      if (zmmtgRoot) {
        zmmtgRoot.style.display = 'block';
      }

      ZoomMtg.init({
        leaveUrl: window.location.origin + `/members/appointments/${appointmentId}?ended=true`,
        patchJsMedia: true,
        leaveOnPageUnload: true,
        success: () => {
          ZoomMtg.join({
            signature: signature,
            meetingNumber: meetingInfo!.meetingNumber,
            passWord: meetingInfo!.password,
            userName: getMemberPublicName() || t('defaultUserName'),
            tk: '',
            zak: '',
            success: () => {
              // we don't do anything here because we are firing events in inMeetingServiceListener
            },
            error: (err: { errorCode: number; errorMessage: string }) => {
              Sentry.captureMessage(`ZoomMtg.join error: ${err.errorMessage}`, {
                level: 'error'
              });
            }
          });
        },
        error: (err: unknown) => {
          Sentry.captureMessage(`ZoomMtg.init error: ${JSON.stringify(err)}`, {
            level: 'error'
          });
          addMessage({
            level: 'error',
            text: t('meetingInitError')
          });
          navigate(`/appointments/${appointmentId}`);
        }
      });
    },
    [meetingInfo, appointmentId, getMemberPublicName, addMessage, navigate, t]
  );

  // Join meeting when we have all required data
  useEffect(() => {
    if (hasJoined.current || !meetingInfo) {
      return;
    }

    const token = getZoomToken();
    if (!token || isLoadingToken) {
      return;
    }

    hasJoined.current = true;
    startMeeting(token);
  }, [meetingInfo, getZoomToken, isLoadingToken, startMeeting]);

  // Show loading state while fetching appointment data
  if (isLoadingEvent || isLoadingToken) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center text-white dark:bg-gray-900 light:bg-gray-900">
        <div className="flex items-center">
          <Spinner label={t('loading')} size={10} />
        </div>
      </div>
    );
  }

  return null;
};

export default ZoomMeeting;
